package com.example.readingnotes.data.repository

import com.example.readingnotes.data.remote.api.ApiClient
import com.example.readingnotes.data.remote.dto.CreatePostRequestDto
import com.example.readingnotes.data.remote.dto.UpdatePostRequestDto
import com.example.readingnotes.data.remote.mapper.toDomain
import com.example.readingnotes.domain.model.Post
import com.example.readingnotes.domain.model.SearchFilters
import com.example.readingnotes.domain.model.SearchHistory
import com.example.readingnotes.domain.repository.PaginatedPosts
import com.example.readingnotes.domain.repository.PostRepository
import com.example.readingnotes.util.Result
import kotlinx.datetime.Instant

class PostRepositoryImpl(
    private val apiClient: ApiClient,
) : PostRepository {
    override suspend fun getPosts(page: Int, pageSize: Int): Result<List<Post>> =
        try {
            val response = apiClient.getPosts(page, pageSize)
            Result.success(response.toDomain())
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun getPostsWithPagination(page: Int, pageSize: Int): Result<PaginatedPosts> =
        try {
            val response = apiClient.getPosts(page, pageSize)
            // For now, assume there are more pages if we receive a full page
            val hasMore = response.size >= pageSize
            Result.success(
                PaginatedPosts(
                    posts = response.toDomain(),
                    currentPage = page,
                    // Simplified - backend should return actual totalPages
                    totalPages = if (hasMore) page + 1 else page,
                    hasMore = hasMore,
                ),
            )
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun getPost(id: String): Result<Post> =
        try {
            val response = apiClient.getPost(id)
            Result.success(response.toDomain())
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun createPost(
        title: String,
        contentMarkdown: String,
        imageUrl: String?,
        imageIds: List<String>,
    ): Result<Post> =
        try {
            val response =
                apiClient.createPost(
                    CreatePostRequestDto(
                        title = title,
                        contentMarkdown = contentMarkdown,
                        imageUrl = imageUrl,
                        imageIds = imageIds,
                    ),
                )
            Result.success(response.toDomain())
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun updatePost(
        id: String,
        title: String?,
        contentMarkdown: String?,
        imageUrl: String?,
        imageIds: List<String>?,
        status: String?,
        totpCode: String?,
    ): Result<Post> =
        try {
            val response =
                apiClient.updatePost(
                    id,
                    UpdatePostRequestDto(
                        title = title,
                        contentMarkdown = contentMarkdown,
                        imageUrl = imageUrl,
                        imageIds = imageIds,
                        status = status,
                    ),
                    totpCode,
                )
            Result.success(response.toDomain())
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun deletePost(id: String, totpCode: String?): Result<Unit> =
        try {
            apiClient.deletePost(id, totpCode)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun updatePostStatus(id: String, status: String, totpCode: String?): Result<Post> =
        try {
            val response = apiClient.updatePostStatus(id, status, totpCode)
            Result.success(response.toDomain())
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun searchPosts(
        query: String,
        page: Int,
        pageSize: Int,
        status: String?,
    ): Result<com.example.readingnotes.domain.repository.SearchResult> =
        try {
            val response = apiClient.searchPosts(query, page, pageSize, status)
            Result.success(
                com.example.readingnotes.domain.repository.SearchResult(
                    results = response.results.map { it.toDomain() },
                    pagination =
                        com.example.readingnotes.domain.repository.SearchPagination(
                            page = response.pagination.page,
                            pageSize = response.pagination.pageSize,
                            total = response.pagination.total,
                            totalPages = response.pagination.totalPages,
                        ),
                    query = response.query,
                ),
            )
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun getSearchSuggestions(
        query: String,
        limit: Int,
    ): Result<List<String>> =
        try {
            val response = apiClient.getSearchSuggestions(query, limit)
            Result.success(response.suggestions)
        } catch (e: Exception) {
            Result.failure(e)
        }

    // ============================================================================
    // Search history methods
    // ============================================================================

    override suspend fun getSearchHistory(): Result<List<SearchHistory>> =
        try {
            val response = apiClient.getSearchHistory()
            Result.success(
                response.history.map { item ->
                    SearchHistory(
                        id = item.id,
                        query = item.query,
                        filters =
                            item.filters?.let { filters ->
                                SearchFilters(
                                    tags = filters.tags,
                                    author = filters.author,
                                    dateFrom = filters.dateFrom,
                                    dateTo = filters.dateTo,
                                    sortBy = filters.sortBy,
                                )
                            },
                        resultsCount = item.resultsCount,
                        createdAt = Instant.parse(item.createdAt),
                    )
                },
            )
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun clearSearchHistory(): Result<Unit> =
        try {
            apiClient.clearSearchHistory()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
}
